import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text, useToast } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import EyeIcon from '../../../../../assets/eye-icon.svg';
import { useNavigate } from 'react-router-dom';
import PaginationComponent from '../../../../../components/Pagination';
import NoDataComponent from '../../../../../components/NoData';
import TableComponent from '../../../../../components/Table';
import InputSearchComponent from '../../../../../components/Common/Form/input-search';
import { formatDate } from '../../../../../utils/helper.util';
import ImageComponent from '../../../../../components/Common/Image';
import { listIgnoredArticles, unignoreArticle } from '../../../../../apis/article.api';
import ToastBtnMessage from '../../../../../components/ToastBtnMessage';

export const IgnoredArticlesTableComponent = () => {
  const navigate = useNavigate();
  const [dataTable, setDataTable] = useState([]);
  const [pagination, setPagination] = useState({
    limit: 6,
    page: 1,
    sortBy: undefined,
    sort: undefined,
    search: undefined
  });
  const toast = useToast();

  const params = {
    ...pagination
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );

  const {
    data: getListIgnoredArticles,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getListIgnoredArticles', filteredParams],
    queryFn: () => listIgnoredArticles(filteredParams)
  });

  const { mutateAsync: postUnignoreArticle } = useMutation({
    mutationFn: unignoreArticle
  });

  const handleChangePage = (page) => {
    setDataTable(() => []);
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const handleUnignoreArticle = async (id) => {
    try {
      await postUnignoreArticle(id);
      toast({
        render: () => (
          <ToastBtnMessage
            title={'Successfully unignored the article!'}
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const convertArticles = useCallback(
    (row) => {
      return (
        <Box
          display={'flex'}
          alignItems={'center'}
          cursor={'pointer'}
          onClick={() => {
            navigate(`/articles/${row?.alternativeId || row?.id}`);
          }}
        >
          <Box position={'relative'} flexShrink={0}>
            {row?.thumbnailUrl ? (
              <ImageComponent
                rounded={'11.38px'}
                w={'40px'}
                h={'40px'}
                src={row?.thumbnailUrl}
                alt="cover"
              />
            ) : (
              <Box
                rounded={'11.38px'}
                w={'40px'}
                h={'40px'}
                bgColor={'#F2F3F7'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                textAlign={'center'}
                fontSize={'10px'}
                lineHeight={'100%'}
              >
                No Image
              </Box>
            )}
          </Box>
          <Text
            fontWeight={800}
            fontSize={'xs'}
            title={row?.title}
            lineHeight={'100%'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
            ml={3}
            _hover={{
              textDecoration: 'underline'
            }}
          >
            {row?.title || 'Untitled'}
          </Text>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertAction = useCallback((row) => {
    return (
      <div
        className="flex gap-2 items-center cursor-pointer hover:opacity-80 w-fit"
        onClick={() => handleUnignoreArticle(row?.id)}
        title="Unignore Article"
      >
        <img src={EyeIcon} className="" alt="icon" />
        <p className="text-growth-green font-[900] text-sm">Unignore</p>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Articles',
        accessor: (row) => convertArticles(row),
        id: 'articleTitle',
        isSortField: true,
        widthCustom: '18rem',
        minWidthCustom: '18rem'
      },
      {
        Header: 'Date Ignored',
        accessor: (row) => <Text fontSize={'xs'}>{formatDate(row?.ignoredAt, true)}</Text>,
        id: 'ignoredAt',
        widthCustom: '10rem',
        minWidthCustom: '10rem',
        isSortField: true
      },
      {
        Header: 'Actions',
        accessor: (row) => convertAction(row),
        widthCustom: '12rem',
        minWidthCustom: '12rem',
        id: 'action'
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertArticles, convertAction, handleSort]
  );

  useEffect(() => {
    if (getListIgnoredArticles) {
      setDataTable(getListIgnoredArticles?.data || []);
    }
  }, [getListIgnoredArticles]);

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={{ base: 0, md: '12px' }}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          gap="12px"
          flexDirection={{ base: 'column', md: 'row' }}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={'360px'}
        flexFlow={'column'}
        overflowY={'hidden'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '8px', // For horizontal scrollbar
            width: '8px' // For vertical scrollbar
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5'
          }
        }}
      >
        <TableComponent
          isLoading={isFetching}
          columns={columns}
          data={dataTable || []}
          headerHeight={'44px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {dataTable?.length === 0 && !isFetching ? (
          <Box minH={'360px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              title="It looks like you haven’t ignored any articles yet."
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>

      {getListIgnoredArticles?.totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={getListIgnoredArticles?.totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
    </Box>
  );
};
